<template>
  <template v-if="$route.name === 'academic.years'">
    <div class="col-12 col-xl-12">
      <div class="container-fluid px-0">
        <div class="row mb-4 g-3 align-items-end">
          <div class="col-12 col-md-6 col-lg-8">
            <div class="d-flex gap-2">
       
            </div>
          </div>

          <div class="col-12 col-md-6 col-lg-4">
            <router-link 
              :to="{ name: 'academic.years.add' }" 
              class="btn btn-secondary btn-sm w-100 shadow-xs"
              v-can="'academic_years.create'"
            >
              <i class="fas fa-plus me-1"></i>
              {{ t('academic.add_year') }}
            </router-link>
          </div>
        </div>
      </div>
   
      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3">{{ t('academic.years_title') }}</h6>
            </div>
          </div>
        </div>
        
        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-2 p-md-4">
            <LoadingSpinner v-if="isLoading" :message="t('academic.loading_years')" />
            
            <template v-else>
              <div class="d-none d-xl-block">
                <div class="table-responsive">
                  <table class="table align-items-center mb-0" style="width: 100%">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 25%">
                          {{ t('academic.year_name') }}
                        </th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 30%">
                          {{ t('academic.year_period') }}
                        </th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 15%">
                          {{ t('academic.status') }}
                        </th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 30%">
                          {{ t('common.actions') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="year in academicYears" :key="year.id">
                        <td>
                          <div class="d-flex flex-column">
                            <strong class="text-sm">{{ year.name }}</strong>
                            <small class="text-xs text-muted">{{ year.year }}</small>
                          </div>
                        </td>
                        <td>
                          <span class="text-sm">
                            {{ formatDate(year.start_date) }} - {{ formatDate(year.end_date) }}
                          </span>
                        </td>
                        <td class="text-center">
                          <span class="badge badge-sm" :class="year.is_active ? 'bg-success' : 'bg-secondary'">
                            {{ year.is_active ? t('academic.active') : t('academic.inactive') }}
                          </span>
                        </td>
                        <td class="align-middle text-center">
                          <div class="d-flex gap-1 justify-content-center">
                            <button 
                              v-if="!year.is_active"
                              class="btn text-success"
                              @click="setActiveYear(year)"
                              :disabled="isSettingActive"
                              :title="t('academic.set_active')"
                              v-can="'academic_years.update'"
                            >
                              <i v-if="isSettingActive && activeYearId === year.id" class="fas fa-spinner fa-spin"></i>
                              <i v-else class="fas fa-check"></i>
                            </button>
                            <router-link 
                              :to="{ name: 'academic.years.edit', params: { id: year.id } }" 
                              class="btn text-primary"
                              :title="t('common.edit')"
                              v-can="'academic_years.update'"
                            >
                              <i class="fas fa-edit"></i>
                            </router-link>
                            <button 
                              class="btn text-danger" 
                              @click="confirmDelete(year)"
                              :disabled="year.is_active || isDeleting"
                              :title="t('common.delete')"
                              v-can="'academic_years.delete'"
                            >
                              <i v-if="isDeleting && deletingId === year.id" class="fas fa-spinner fa-spin"></i>
                              <i v-else class="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="academicYears.length === 0">
                        <td colspan="4" class="text-center py-4 text-muted">
                          <i class="fas fa-calendar-times fa-2x mb-2"></i>
                          <p>{{ t('academic.no_years_found') }}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div class="d-xl-none">
                <div v-if="academicYears.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-calendar-times fa-2x mb-2"></i>
                  <p>{{ t('academic.no_years_found') }}</p>
                </div>
                
                <div v-else class="row g-3">
                  <div class="col-12" v-for="year in academicYears" :key="year.id">
                    <div class="card shadow-xs border">
                      <div class="card-body">
                        <div class="d-flex justify-content-between mb-2">
                          <span class="text-sm">{{ t('academic.year_name') }}:</span>
                          <div class="text-end">
                            <strong class="text-sm">{{ year.name }}</strong>
                            <br>
                            <small class="text-xs text-muted">{{ year.year }}</small>
                          </div>
                        </div>
                        
                        <div class="d-flex justify-content-between mb-2">
                          <span class="text-sm">{{ t('academic.year_period') }}:</span>
                          <span class="text-sm">
                            {{ formatDate(year.start_date) }} - {{ formatDate(year.end_date) }}
                          </span>
                        </div>
                        
                        <div class="d-flex justify-content-between mb-3">
                          <span class="text-sm">{{ t('academic.status') }}:</span>
                          <span class="badge badge-sm" :class="year.is_active ? 'bg-success' : 'bg-secondary'">
                            {{ year.is_active ? t('academic.active') : t('academic.inactive') }}
                          </span>
                        </div>
                        
                        <div class="d-flex justify-content-end gap-2">
                          <button 
                            v-if="!year.is_active"
                            class="btn btn-success btn-sm"
                            @click="setActiveYear(year)"
                            :disabled="isSettingActive"
                            v-can="'academic_years.update'"
                          >
                            <i v-if="isSettingActive && activeYearId === year.id" class="fas fa-spinner fa-spin me-1"></i>
                            <i v-else class="fas fa-check me-1"></i>
                            {{ t('academic.set_active') }}
                          </button>
                          <router-link 
                            :to="{ name: 'academic.years.edit', params: { id: year.id } }" 
                            class="btn text-primary "
                            v-can="'academic_years.update'"
                          >
                            <i class="fas fa-edit me-1"></i>
                          </router-link>
                          <button 
                            class="btn text-danger" 
                            @click="confirmDelete(year)"
                            :disabled="year.is_active || isDeleting"
                            v-can="'academic_years.delete'"
                          >
                            <i v-if="isDeleting && deletingId === year.id" class="fas fa-spinner fa-spin me-1"></i>
                            <i v-else class="fas fa-trash me-1"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <DeleteDialog 
      ref="deleteDialog"
      entity-name="AcademicYear"
      :entity-label="t('academic.year')"
      @delete="deleteYear"
    />
  </template>
  
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import DeleteDialog from '@/components/common/DeleteDialog.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t, locale } = useI18n();
const toast = useToast();

const academicYears = ref([]);
const isLoading = ref(false);
const isDeleting = ref(false);
const deletingId = ref(null);
const isSettingActive = ref(false);
const activeYearId = ref(null);
const deleteDialog = ref(null);

const loadAcademicYears = async () => {
  isLoading.value = true;
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
  } catch (error) {
    notifyError(error, 'errors.load_failed');
  } finally {
    isLoading.value = false;
  }
};

const setActiveYear = async (year) => {
  isSettingActive.value = true;
  activeYearId.value = year.id;
  try {
    await api.post(`/academic-years/${year.id}/set-active`);
    toast.success(t('academic.year_activated'));
    await loadAcademicYears();
  } catch (error) {
    notifyError(error, 'errors.operation_failed');
  } finally {
    isSettingActive.value = false;
    activeYearId.value = null;
  }
};

const confirmDelete = (year) => {
  deleteDialog.value.openDialog(year.id, t('academic.year') + ' ' + year.name);
};

const deleteYear = async (id) => {
  isDeleting.value = true;
  deletingId.value = id;
  try {
    await api.delete(`/academic-years/${id}`);
    toast.success(t('academic.year_deleted'));
    await loadAcademicYears();
  } catch (error) {
    notifyError(error, 'errors.operation_failed');
  } finally {
    isDeleting.value = false;
    deletingId.value = null;
  }
};


const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(locale.value);
};

function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
  return toast.error(extractApiMessage(error, fallbackKey));
}

onMounted(() => {
  loadAcademicYears();
});
</script>